import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dwrClassesById } from '@/data/classes';
import { InkBlock } from '@/components/atoms';
import {
  AppTopbar,
  ConfirmDialog,
  FullSheet,
  LevelsRail,
  MobileAsideDrawer,
  UndoToast,
} from '@/components/organisms';
import { AppShell, Aside } from '@/components/templates';
import { useUndoCountdown } from '@/hooks/useUndoCountdown';
import { cn } from '@/lib/utils';
import {
  clearCharacter,
  exportCharacterAsJson,
  importCharacterFromJson,
  loadCharacter,
  saveCharacter,
} from '@/services/characterStorage';
import { isLevelUpAvailable } from '@/services/levelUp';
import { readUndoSnapshot, type UndoSnapshot } from '@/services/undoSnapshot';
import type { Character } from '@/schemas/character';

const UNDO_COUNTDOWN_SECONDS = 8;

// page — DWR Hub: visualizzazione personaggio completo + storia level-up.
// Rewrite cosmetico (PR 2 Fase 2): da Card shadcn neutra a layout DWR
// (AppShell + AppTopbar variant="hub" + FullSheet + LevelsRail).
// Toolbar Salva/Carica/Elimina dentro topbar actions slot, accanto al CTA
// primario HubCta "Sali di livello".
//
// **PR 11**: mount `<UndoToast>` post level-up apply (snapshot scritto da
// LevelUpFlowPage in sessionStorage, qui letto al mount + visualizzato con
// countdown 8s). `<ConfirmDialog destructive>` per delete personaggio
// (chiusura FI-47 residuo).

export function CharacterViewPage() {
  const [character, setCharacter] = useState<Character | null>(() => loadCharacter());
  const [error, setError] = useState<string | null>(null);
  const [undoSnapshot, setUndoSnapshot] = useState<UndoSnapshot | null>(null);

  useEffect(() => {
    setCharacter(loadCharacter());
    // Read-once: il service rimuove subito lo snapshot dal sessionStorage.
    const snapshot = readUndoSnapshot();
    if (snapshot) setUndoSnapshot(snapshot);
  }, []);

  const undoSecondsLeft = useUndoCountdown(UNDO_COUNTDOWN_SECONDS, undoSnapshot !== null, () =>
    setUndoSnapshot(null),
  );

  function handleUndo() {
    if (!undoSnapshot) return;
    saveCharacter(undoSnapshot.character);
    setCharacter(undoSnapshot.character);
    setUndoSnapshot(null);
  }

  if (!character) {
    return <EmptyState onCharacterLoaded={setCharacter} onError={setError} />;
  }

  // **FI-53 (Sprint F)**: aside contenuto riusato in desktop `<Aside>` e
  // mobile `<MobileAsideDrawer>` via stessa variabile.
  const hubAsideContent = <HubAside character={character} />;

  return (
    <>
      <AppShell
        header={
          <AppTopbar
            variant="hub"
            brand={
              <HubBrand
                mobileDrawerContent={hubAsideContent}
                watermarkUrl={`/classes/${character.classId}.png`}
              />
            }
            center={<HubCharLabel character={character} />}
            actions={
              <HubActions
                character={character}
                onCharacterChange={setCharacter}
                onError={setError}
              />
            }
          />
        }
        main={
          <div className="space-y-4">
            {error ? (
              <p
                className="border-accent-dwr text-accent-dwr bg-paper-bright border px-3 py-2 text-sm"
                role="alert"
              >
                {error}
              </p>
            ) : null}
            <FullSheet character={character} />
          </div>
        }
        aside={<Aside watermarkUrl={`/classes/${character.classId}.png`}>{hubAsideContent}</Aside>}
      />
      <UndoToast
        open={undoSnapshot !== null}
        message={undoSnapshot?.message ?? ''}
        secondsLeft={undoSecondsLeft}
        onUndo={handleUndo}
        onDismiss={() => setUndoSnapshot(null)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Empty state — page-specific, not reusable: PG mancante in localStorage
// ---------------------------------------------------------------------------

interface EmptyStateProps {
  onCharacterLoaded: (c: Character) => void;
  onError: (msg: string | null) => void;
}

function EmptyState({ onCharacterLoaded, onError }: EmptyStateProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleImport(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const result = reader.result;
        if (typeof result !== 'string') {
          throw new Error('Risultato della lettura file non è una stringa');
        }
        const imported = importCharacterFromJson(result);
        saveCharacter(imported);
        onCharacterLoaded(imported);
        onError(null);
      } catch (err) {
        onError(err instanceof Error ? err.message : "Errore durante l'import");
      }
    };
    reader.readAsText(file);
  }

  return (
    <AppShell
      header={<AppTopbar variant="hub" brand={<HubBrand mobileDrawerContent={null} />} />}
      main={
        <div className="bg-paper-bright border-rule flex flex-col items-center gap-4 border px-7 py-12 text-center">
          <h1 className="font-display text-2xl font-bold tracking-tight">
            Non hai ancora un personaggio
          </h1>
          <p className="text-ink-soft max-w-md italic">
            Crea un nuovo personaggio o carica un salvataggio esistente per cominciare.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <InkBlock asChild>
              <Link to="/builder">Crea il tuo personaggio</Link>
            </InkBlock>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="font-display border-ink-soft text-ink hover:bg-paper-soft border bg-transparent px-4 py-2 text-xs tracking-[0.08em] uppercase transition"
              title="Carica un personaggio da un salvataggio"
            >
              Carica salvataggio
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleImport(file);
              e.target.value = '';
            }}
          />
        </div>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Topbar sub-componenti (page-specific)
// ---------------------------------------------------------------------------

function HubBrand({
  mobileDrawerContent,
  watermarkUrl,
}: {
  mobileDrawerContent: ReactNode;
  watermarkUrl?: string;
}) {
  return (
    <>
      {/* Mobile (<900px): drawer trigger se è presente contenuto aside (Hub
       *  popolato). Sull'empty state passiamo `null` → niente trigger, il
       *  brand DW resta visibile come unico elemento. **FI-53 Sprint F.** */}
      {mobileDrawerContent ? (
        <span className="min-[900px]:hidden">
          <MobileAsideDrawer title="Storico livelli" watermarkUrl={watermarkUrl}>
            {mobileDrawerContent}
          </MobileAsideDrawer>
        </span>
      ) : null}
      {/* Desktop (≥900px): brand DW + nome app. Su empty state mobile
       *  resta visibile da solo (nessun drawer da aprire). */}
      <span
        className={cn(
          'bg-ink text-paper-bright font-display grid h-7 w-7 place-items-center text-sm font-bold tracking-normal',
          mobileDrawerContent ? 'max-[900px]:hidden' : undefined,
        )}
        aria-hidden
      >
        DW
      </span>
      <span
        className={cn(
          'font-display hidden text-[0.95rem] font-bold tracking-[0.08em] sm:inline',
          mobileDrawerContent ? 'max-[900px]:hidden' : undefined,
        )}
      >
        Dungeon World <em className="font-normal italic opacity-[0.65]">Rebrewed</em>
      </span>
    </>
  );
}

function HubCharLabel({ character }: { character: Character }) {
  const classDef = dwrClassesById[character.classId];
  const className = classDef?.name ?? character.classId;
  return (
    <span className="text-ink-soft text-[0.95rem] italic">
      <strong className="text-ink font-semibold not-italic">{character.name}</strong>
      {character.title ? <span> {character.title}</span> : null}
      <em>, {className}</em>
      <span className="mx-[0.5em] opacity-55">·</span>
      <span className="font-display text-[0.78rem] tracking-[0.08em] not-italic">
        L{character.level}
      </span>
    </span>
  );
}

interface HubActionsProps {
  character: Character;
  onCharacterChange: (c: Character | null) => void;
  onError: (msg: string | null) => void;
}

function HubActions({ character, onCharacterChange, onError }: HubActionsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  function handleExport() {
    const json = exportCharacterAsJson(character);
    downloadJson(json, `${character.name}-${character.id}.json`);
  }

  function handleImport(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const result = reader.result;
        if (typeof result !== 'string') {
          throw new Error('Risultato della lettura file non è una stringa');
        }
        const imported = importCharacterFromJson(result);
        saveCharacter(imported);
        onCharacterChange(imported);
        onError(null);
      } catch (err) {
        onError(err instanceof Error ? err.message : "Errore durante l'import");
      }
    };
    reader.readAsText(file);
  }

  // FI-47 residuo (PR 11): dialog DWR custom invece di confirm() JS native.
  function handleDeleteConfirmed() {
    clearCharacter();
    onCharacterChange(null);
    setDeleteDialogOpen(false);
  }

  // CTA "Sali a L{N+1}" vive solo in `<LevelsRail>` aside (pattern mockup
  // hub.jsx:71). Hub mobile cleanup (FI-62): topbar actions ridotte a 3
  // ghost button (Salva / Carica / Elimina) — risolve overflow su 375.
  return (
    <>
      <GhostButton onClick={handleExport} title="Scarica un salvataggio del personaggio">
        Salva
      </GhostButton>
      <GhostButton
        onClick={() => fileInputRef.current?.click()}
        title="Carica un personaggio da un salvataggio"
      >
        Carica
      </GhostButton>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImport(file);
          e.target.value = '';
        }}
      />
      <GhostButton
        onClick={() => setDeleteDialogOpen(true)}
        tone="danger"
        title="Elimina il personaggio (non si può annullare)"
      >
        Elimina
      </GhostButton>
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        variant="destructive"
        title="Eliminare il personaggio?"
        description="L'operazione non si può annullare. Tutti i livelli salvati andranno persi."
        confirmLabel="Sì, elimina"
        cancelLabel="Annulla"
        onConfirm={handleDeleteConfirmed}
      />
    </>
  );
}

interface GhostButtonProps {
  children: ReactNode;
  onClick?: () => void;
  title?: string;
  tone?: 'default' | 'danger';
}

// page-specific, not reusable: ghost button per la HubTopbar — sarà
// generalizzato come <Chip variant="ghost" as="button"> in PR 4-7 di Fase 2
// (cluster .ghost-btn / .ghost-btn--danger del mockup).
function GhostButton({ children, onClick, title, tone = 'default' }: GhostButtonProps) {
  const toneClass =
    tone === 'danger'
      ? 'border-[#a4421e] text-[#a4421e] hover:bg-[rgba(164,66,30,0.08)]'
      : 'border-ink-soft text-ink hover:bg-paper-soft';
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`font-display border bg-transparent px-3 py-1.5 text-xs tracking-[0.08em] uppercase transition ${toneClass}`}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Aside content
// ---------------------------------------------------------------------------

function HubAside({ character }: { character: Character }) {
  const navigate = useNavigate();
  const levelUpAvailable = isLevelUpAvailable(character);
  const tooltip = asideTooltip(character, levelUpAvailable);

  return (
    <LevelsRail
      character={character}
      onLevelUp={
        levelUpAvailable
          ? () => {
              void navigate('/character/level-up');
            }
          : undefined
      }
      onEditLevel={(level) => {
        // FI-46 PR-B + FI-46bis: navigazione retroattiva all'edit del livello.
        // - L1 → builder pre-popolato (FI-46bis)
        // - L2+ → flow level-up edit-mode (FI-46 PR-B)
        if (level === 1) {
          void navigate('/character/edit-l1');
          return;
        }
        void navigate(`/character/edit-level-up/${level}`);
      }}
      levelUpDisabledTooltip={tooltip}
    />
  );
}

function asideTooltip(character: Character, levelUpAvailable: boolean): string | undefined {
  if (levelUpAvailable) return undefined;
  if (character.pastTen === 'addio')
    return "Il personaggio è ritirato (L'Addio). Non può più salire di livello.";
  return `Il personaggio è al livello massimo (${character.level}).`;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function downloadJson(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
